import React, { useEffect, useState } from "react";
import { Box, Button, ButtonGroup, Paper, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { TabPanel } from "../components/TabPanel";
import { TabItem } from "../components/Tab";
import {
  getProjectDrillholes,
  fetchDrillholesIDsFromServer,
  fetchDrillholeFromServer,
  syncDrillhole,
} from "../api/drillhole";
import { useProject } from "../hooks/project";
import { decrypt, createGuid } from "../utils/cryptoHelper";
import { Center } from "@react-three/drei";
import { createLOCATable } from "../utils/initTable";
import { computeValues, computeValues0Rows, computeValuesHdph, defaultValues, defaultValues0Rows, mkDefaultRowGeneric } from "../utils/createTable";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const DataView = () => {
  const theme = useTheme();
  const [DrillHoleName, setDrillHoleName] = useState([]);
  /**
   * @type {[string | null, import("react").Dispatch<import("react").SetStateAction<string | null>>]}
   */
  const [selectedDrillHole, setSelectedDrillHole] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [tabList, setTabList] = useState([]);
  const [currentData, setCurrentData] = useState({ columns: [], rows: [] });
  const [allDrillholeData, setAllDrillholeData] = useState([]);
  const [token, setToken] = useState();
  const [newDrillholeName, setNewDrillholeName] = useState("");
  const project = useProject();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [status, setStatus] = useState("Ready");

  // Styles
  const containerStyle = {
    display: "flex",
    height: "100%",
    backgroundColor: theme.palette.background.default,
    padding: "16px",
    gap: "16px",
  };

  const leftBoxStyle = {
    flex: "0 0 300px",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    overflow: "hidden",
  };

  const rightContentStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    overflow: "hidden",
    width: "100%",
  };

  const tabContainerStyle = {
    display: "flex",
    gap: "8px",
    padding: "8px",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px 8px 0 0",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    overflow: "auto",
    minHeight: "calc(var(--tab-heigh) + 16px + 4px)",
  };

  const tabPanelContainerStyle = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "0 0 8px 8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    flex: 1,
    overflow: "auto",
  };

  useEffect(() => {
    handleData();

    const token = window.localStorage.getItem("token");
    const user = JSON.parse(decrypt(token));
    setToken(user);
    console.log("-----user------", user);
  }, [project.project_id]);

  const dataTypes = [
    "CDIA",
    "CORE",
    "FLSH",
    "FRAC",
    "GEOL",
    "HORN",
    "HDPH",
    "IMPK",
    "IPRG",
    "IVAN",
    "MONG",
    "PTIM",
    "SAMP",
    "TELE",
  ];

  // data types to show by default when adding new drillhole
  const defaultDataTypes = [
    "LOCA",
    "HORN",
    "FLSH",
    "HDPH",
    "PTIM",
    "CDIA",
    "ISPT",
    "MONG",
    "CORE",
    "FRAC",
    "GEOL",
    "DETL",
    "IVAN",
    "IPRG",
    "PMTG",
    "TELE",
    "IMPK",
    "RPLT",
  ];

  const handleData = async () => {
    const getServerIDs = () =>
      new Promise((resolve) => {
        fetchDrillholesIDsFromServer(
          project.project_id,
          (cloudDrillholeIds) => {
            resolve(cloudDrillholeIds);
          }
        );
      });

    const serverAllIDs = await getServerIDs();
    const availableHoles = [];

    await Promise.all(
      serverAllIDs.map(async (drillHoleID) => {
        const cloudDrillhole = await fetchDrillholeFromServer(
          project.project_id,
          drillHoleID,
          (drillHoleData) => {
            setAllDrillholeData((prevData) => [...prevData, drillHoleData]);

            const newHole = {
              id: `${drillHoleData.id}`,
              label: drillHoleData.LOCA_ID,
              children: [
                {
                  id: `${drillHoleData.id}|${drillHoleData.LOCA_ID}|LOCA`,
                  label: "LOCA",
                  data: {
                    drillId: drillHoleData.id,
                    table: drillHoleData.LOCA_ID,
                    type: "LOCA",
                  },
                },
              ],
            };

            const availableTabs = new Set();
            availableTabs.add("LOCA");

            // we want to show all tables even if there is no row in the database,
            // because users need to be able to add the first row.
            defaultDataTypes.forEach((type) => {
              if (availableTabs.has(type)) {
                return;
              }
              availableTabs.add(type);
              newHole.children.push({
                id: `${drillHoleData.id}|${drillHoleData.LOCA_ID}|${type}`,
                label: type,
                data: {
                  drillId: drillHoleData.id,
                  table: drillHoleData.LOCA_ID,
                  type: type,
                },
              });
            });

            // dataTypes.forEach((type) => {
            //   if (availableTabs.has(type)) {
            //     return;
            //   }
            //   availableTabs.add(type);
            //   newHole.children.push({
            //     id: `${drillHoleData.id}|${drillHoleData.LOCA_ID}|${type}`,
            //     label: type,
            //     data: {
            //       drillId: drillHoleData.id,
            //       table: drillHoleData.LOCA_ID,
            //       type: type,
            //     },
            //   });
            // });

            availableHoles.push(newHole);
          }
        );
      })
    );

    setDrillHoleName(availableHoles.sort((a, b) => a.label.localeCompare(b.label)));
  };

  const updateTabData = (drillId, type) => {
    return allDrillholeData.map((drillHole) => {
      if (drillHole.id === drillId) {
        if (type === "LOCA") {
          const locaKeys = Object.keys(drillHole).filter(
            (key) =>
              !Array.isArray(drillHole[key]) &&
              typeof drillHole[key] !== "object"
          );

          const columns = locaKeys.map((key) => ({
            field: key,
            headerName: key,
            autoWidth: true,
            // flex: 1,
            editable:
              token.id === "4dbcf2f8-be13-485b-b99b-f31cfa4f1c19"
                ? false
                : !(
                    key.includes("id") ||
                    key.includes("ID") ||
                    key.includes("created") ||
                    key.includes("modified") ||
                    key.includes("active")
                  ),
          }));

          const rows = [
            {
              id: 0,
              ...locaKeys.reduce((acc, key) => {
                acc[key] = drillHole[key];
                return acc;
              }, {}),
            },
          ].filter(
            (row) => row.active.toString() === '1' || row.active.toString() === 'true'
          );

          setCurrentData({ columns, rows });
        } else {
          const typeL = type.toLowerCase();
          const typeData = drillHole[typeL];
          // horn only has 1 row so it's an object rather than an array
          const rows_ = typeL === 'horn' ? [typeData] : typeData

          let columns = []
          if (rows_.length > 0) {
            columns = Object.keys(rows_[0]).map((key) => ({
              field: key,
              headerName: key,
              autoWidth: true,
              // flex: 1,
              editable:
                token.id === "4dbcf2f8-be13-485b-b99b-f31cfa4f1c19"
                  ? false
                  : !(
                    key.includes("id")
                    || key.includes("ID")
                    || key.includes("created")
                    || key.includes("modified")
                    || key.includes("active")
                    || (
                      // uppercase
                      Object.keys(computeValuesHdph).includes(type)
                      && (
                        key === `${type}_TOP`
                        || key === `${type}_BASE`
                      )
                    )
                  ),
            }));
          }

          let rows = rows_
            .filter(
              (row) => row.active.toString() === '1' || row.active.toString() === 'true'
            )
            .map((item, index) => ({
              id: index,
              ...item,
            }));

          // defaultValues0Rows have 0 rows by default. but we still
          // need to show their columns. so we get columns from
          // computeValues0Rows and defaultValues0Rows
          if (
            // check if uppercase table name is 0 row by default
            Object.keys(defaultValues0Rows).includes(type)
            && rows.length === 0
            && columns.length === 0
          ) {
            const non_editables = {
              beforeAll: [
                ["id", "ID"],
                ["location_id", "location_id"],
                ["LOCA_ID", "LOCA_ID"],
              ],
              afterAll: [
                ["created_at", "Created At"],
                ["modified_at", "Modified At"]
              ]
            }

            for (const [field, headerName] of non_editables.beforeAll) {
              columns.push({
                field,
                headerName,
                autoWidth: true,
                editable: false,
              })
            }

            const hdph_id = drillHole['hdph'][0].id;
            const kv = [...computeValues0Rows[type](hdph_id), ...defaultValues0Rows[type]]
            for (const [fieldName, _value] of kv) {
              columns.push({
                field: fieldName,
                headerName: fieldName,
                autoWidth: true,
                // flex: 1,
                editable:
                  !(
                    fieldName.includes("id") ||
                    fieldName.includes("ID") ||
                    fieldName.includes("created") ||
                    fieldName.includes("modified")
                  ),
              })
            }

            columns.push({
                field: 'active',
                headerName: 'Active',
                autoWidth: true,
                editable: true,
            })

            for (const [field, headerName] of non_editables.afterAll) {
              columns.push({
                field,
                headerName,
                autoWidth: true,
                editable: false,
              })
            }

            rows = []
          }

          setCurrentData({ columns, rows });
        }

        return (
          <>
            {drillHole.LOCA_ID}
            <br />
            {type}
            {drillId}
          </>
        );
      }
      return null;
    });
  }

  const handleItemSelectionToggle = (event, itemId, isSelected) => {
    if (isSelected && itemId.includes("|")) {
      const [drillId, locaId, type] = itemId.split("|");

      const idx = tabList.findIndex(tab => {
        const tableName = tab.props.children[0]
        const type_ = tab.props.children[2];
        return tableName === locaId && type_ === type
      });

      const tabExists = idx !== -1;

      // regardless if tab exists, we need to change the table data
      const labels = updateTabData(drillId, type);
      labels.forEach(label => {
        if (label == null) {
          return
        }
        // don't add new tab if tab already exists
        if (!tabExists && !tabList.includes(label)) {
          setTabList([...tabList, label]);
        }
      })

      // reuse existing tab where possible
      if (tabExists) {
        setTabValue(idx);
      } else {
        setTabValue(tabList.length);
      }
    }
  };

  const handleTabClick = (index) => {
    if (tabValue === index) {
      return
    }

    setTabValue(index);

    const tableName = tabList[index].props.children[0];
    const type = tabList[index].props.children[2];
    const drillId = tabList[index].props.children[3];

    updateTabData(drillId, type);

    // update selected tree view item to match current tab
    setSelectedDrillHole([drillId, tableName, type].join('|'))
  };

  const handelTabCloseClick = (index) => {
    const newTabList = [...tabList];
    newTabList.splice(index, 1);
    setTabList(newTabList);

    // this fixes: open two tabs, close 1st tab. data is now blank
    let newTabValue = tabValue;
    if (index <= tabValue) {
      if (index === 0) {
        newTabValue = 0;
      } else {
        newTabValue = index - 1;
      }
    }
    setTabValue(newTabValue);

    // this fixes: open two tabs, switch 1st tab, close 1st tab. data is not updated.
    if (newTabList.length === 0) {
      setSelectedDrillHole(null);
      return
    }
    const drillholeType = newTabList[newTabValue].props.children[2];
    const drillId = newTabList[newTabValue].props.children[3];
    updateTabData(drillId, drillholeType);

    // if the selected item in the RichTreeView is equal to drillholeType, unselect it.
    if (selectedDrillHole == null) {
      return;
    }
    const selectedType = selectedDrillHole.split("|")[2];
    if (selectedType === drillholeType) {
      setSelectedDrillHole(null);
    }
  };

  const handleTableValueChange = (tabName, newValue) => {
    console.log("----handleTableValueChange----", newValue);
    if (newValue === 0) {
      return
    }
    const tableName = tabName.props.children[2].toLowerCase();
    //update Data
    const updatedDrillholeData = allDrillholeData.map((drillHole) => {
      // location id is the primary key of LOCA table
      let location_id = newValue.location_id
      if (location_id == null) {
        if (newValue.id == null) {
          // console.warn('location_id and id not in newValue, check table ' + tableName)
          return drillHole;
        } else {
          location_id = newValue.id
        }
      }

      if (drillHole.id === location_id) {
        let updatedDrillHole = { ...drillHole };
        if (Array.isArray(drillHole[tableName])) {
          const index = drillHole[tableName].findIndex(
            (item) => item.id === newValue.id
          );
          if (index !== -1) {
            const updatedArray = [...drillHole[tableName]];
            updatedArray[index] = newValue;
            updatedArray[index].modified_at = new Date().toISOString();
            updatedDrillHole = {
              ...drillHole,
              [tableName]: updatedArray,
            };
          }
        } else if (tableName === 'horn') {
          updatedDrillHole.horn = newValue;
          updatedDrillHole.horn.modified_at = new Date().toISOString();
        } else {
          // LOCA table - update keys in updatedDrillHole, but be careful
          // to not remove keys already existing (those are other table's data)
          updatedDrillHole = {
            ...updatedDrillHole,
            ...newValue,
            modified_at: new Date().toISOString(),
          };
        }

        // if table is hdph, also change top and base columns
        // for all tables that are related to hdph.
        // relation is via hdph_id.
        if (tableName === 'hdph') {
          const hdph_id = newValue.id;
          for (const extraTableName of Object.keys(computeValuesHdph)) {
            const topColName = `${extraTableName}_TOP`;
            const baseColName = `${extraTableName}_BASE`;
            const extraTableNameL = extraTableName.toLowerCase();

            updatedDrillHole[extraTableNameL] =
              drillHole[extraTableNameL].map(row => {
                if (row.hdph_id === hdph_id) {
                  const newRow = { ...row };
                  newRow[topColName] = newValue['HDPH_TOP'];
                  newRow[baseColName] = newValue['HDPH_BASE'];
                  newRow.modified_at = new Date().toISOString();
                  return newRow;
                }
                return row;
              })
          }
        }

        setStatus("Saving changes...");
        syncDrillhole(
          project.project_id,
          updatedDrillHole,
          location_id,
          async (data) => {
            console.log("---sync success---", data);
            displaySaveConfirmation(setStatus);
          }
        );

        return updatedDrillHole;
      }
      return drillHole;
    });

    setAllDrillholeData(updatedDrillholeData);

    setCurrentData((prev) => ({
      columns: prev.columns,
      rows: prev.rows.map(row => {
        if (row.id === newValue.id) {
          return newValue;
        }
        return row;
      }),
    }));
  };

  const handleAddDrillHole = () => {
    // 使用 createTable 函數生成所有所需數據
    const drillholeName = newDrillholeName;
    const tableData = createLOCATable(drillholeName, project);

    // 更新左側樹狀結構
    setDrillHoleName(
      (prev) => [...prev, tableData.drillHole].sort((a, b) => a.label.localeCompare(b.label))
    );

    // 更新標籤列表
    setTabList((prev) => [...prev, tableData.tabLabel]);

    // 將焦點設置到新添加的標籤
    setTabValue(tabList.length);

    // 設置表格數據
    setCurrentData({
      columns: tableData.columns,
      rows: [tableData.row],
    });

    setNewDrillholeName("");

    // insert row to LOCA table.
    // syncDrillhole can insert row to LOCA table, but it can't handle `undefined` id
    // so we just generate a random (but valid) GUID. it would not match any existing row,
    // so the backend would add our row to LOCA table with that id.

    const data = tableData.row;
    const location_id = data.id;

    for (const [tableName, rows] of Object.entries(tableData.extra)) {
      data[tableName] = rows;
    }

    setAllDrillholeData((prev) => [...prev, data]);

    setStatus("Saving changes...");
    syncDrillhole(
      project.project_id,
      data,
      location_id,
      async (data) => {
        console.log("---sync success---", data);
        displaySaveConfirmation(setStatus);
      }
    );
  };

  const handleAddRow = () => {
    // loca_id is the name of the drillhole
    const drillholeName = tabList[tabValue].props.children[0];
    const tableName = tabList[tabValue].props.children[2];
    // unique id of LOCA table - also the location id for every other table
    const drillId = tabList[tabValue].props.children[3];

    const defaults = defaultValues[tableName] ?? defaultValues0Rows[tableName];
    // this either takes no args or the hdph_id arg
    const compute = computeValues[tableName] ?? computeValues0Rows[tableName];

    const drillhole = allDrillholeData.find(drillhole => {
      return (drillhole.id === drillId)
    })
    const hdph_id = drillhole['hdph'][0].id;

    // this generates a unique id for this table, and handles the
    // active, create, modified date cols.
    const row = mkDefaultRowGeneric(drillId, drillholeName, defaults, compute, hdph_id)

    setCurrentData((prev) => ({
      columns: prev.columns,
      rows: [...prev.rows, row],
    }));

    function createNewRow(updatedDrillhole, drillholeType, row_) {
      // we need to copy the array, otherwise we would modify the one that
      // `drillhole` is pointing to.
      // updatedDrillhole needs to have an array that's a new object.
      if (Array.isArray(updatedDrillhole[drillholeType])) {
        return [...updatedDrillhole[drillholeType], row_];
      } else {
        // this cannot be LOCA or HORN as we hide the button. so this means
        // it's a table with arrays as rows, but there's no data at all
        // for this table.
        // this is a new array as well.
        return [row_];
      }
    }

    /**
     * Add the new row to the correct drillhole object, returning a copy of the new
     * drillhole object.
     *
     * also if new row is in the HDPH table, also add a new row in tables
     * that require HDPH to compute default values
     */
    const addRowToDrillhole = (drillhole) => {
      const updatedDrillhole = { ...drillhole };
      const drillholeType = tableName.toLowerCase();
      updatedDrillhole[drillholeType] = createNewRow(updatedDrillhole, drillholeType, row);

      // if new row is in the HDPH table, also add a new row in tables
      // that require HDPH to compute default values
      if (drillholeType === 'hdph') {
        for (const extraTableName of Object.keys(computeValuesHdph)) {
          /**
           * this compute function takes in an arg of the hdph row
           * @type {(hdph: object) => Array<[string, any]>}
           */
          const compute = computeValuesHdph[extraTableName];
          const extraRow = mkDefaultRowGeneric(drillId, drillholeName, [], compute, row);

          const extraTableNameL = extraTableName.toLowerCase()
          updatedDrillhole[extraTableNameL] = createNewRow(
            updatedDrillhole, extraTableNameL, extraRow
          );
        }
      }

      return updatedDrillhole;

    };

    // syncDrillhole needs a single object of this drillhole (eg {...LOCA, FLSH: {...}, })
    let newDrillholeData = null;
    for (const drillhole of allDrillholeData) {
      if (drillhole.id === drillId) {
        newDrillholeData = addRowToDrillhole(drillhole);
        break;
      }
    }

    // set newDrillholeData (don't rerun addRowToDrillhole, as it would
    // generate a new unique id)
    setAllDrillholeData(((prev) => {
        return prev.map(drillhole => {
          if (drillhole.id === drillId) {
            return newDrillholeData;
          }
          return drillhole;
        });
      }))

    setStatus("Saving changes...");
    syncDrillhole(
      project.project_id,
      newDrillholeData,
      drillId,
      async (data) => {
        console.log("---add new row: sync success---", data);
        displaySaveConfirmation(setStatus);
      }
    );
  }

  const handleDeleteRow = () => {
    // rowSelectionModel is an array of row IDs (unique id for this table)

    // unique id of LOCA table - also the location id for every other table
    const drillId = tabList[tabValue].props.children[3];
    const tableName = tabList[tabValue].props.children[2].toLowerCase();

    // set active=0
    const newDrillholeData = allDrillholeData.map(drillhole => {
      if (drillhole.id === drillId) {
        const table = drillhole[tableName];
        const hdph_ids = [];
        const newTable = table.map(row => {
          if (rowSelectionModel.includes(row.id)) {
            if (tableName === 'hdph') {
              hdph_ids.push(row.id);
            }
            return { ...row, active: false };
          }
          return row;
        });

        const newDrillhole = { ...drillhole };
        newDrillhole[tableName] = newTable;

        // if hdph row is deleted, also delete the corresponding rows in tables
        // that are related to hdph.
        // the row to delete has the same hdph_id as the row that was deleted.
        if (hdph_ids.length > 0) {
          for (const extraTableName of Object.keys(computeValuesHdph)) {
            const extraTableNameL = extraTableName.toLowerCase();
            const extraTable = drillhole[extraTableNameL];

            // this is safe as we don't change hdph_id after the above loop.
            // there is nothing to mutate.
            // eslint-disable-next-line no-loop-func
            const newTableExtra = extraTable.map(row => {
              if (hdph_ids.includes(row.hdph_id)) {
                return { ...row, active: false };
              }
              return row;
            });

            newDrillhole[extraTableNameL] = newTableExtra;
          }
        }

        return newDrillhole;
      }
      return drillhole;
    });

    setAllDrillholeData(newDrillholeData)

    // syncDrillhole needs a single object of this drillhole (eg {...LOCA, FLSH: {...}, })
    const data = newDrillholeData.find(drillhole => drillhole.id === drillId)
    setStatus("Saving changes...");
    syncDrillhole(
      project.project_id,
      data,
      drillId,
      async (data) => {
        console.log("---delete row: sync success---", data);
        displaySaveConfirmation(setStatus);
      }
    );

    setCurrentData((prev) => ({
      columns: prev.columns,
      rows: prev.rows.filter(row => !rowSelectionModel.includes(row.id))
    }))

    setRowSelectionModel([])
  }

  // this is uppercase
  const tableName = tabList[tabValue]?.props?.children?.[2];

  // add and remove rows are instead controlled by adding/removing from hdph.
  const isTableComputedFromHdph = Object.keys(computeValuesHdph).includes(tableName);

  const areButtonsDisabled =
    tableName === 'LOCA'
    || tableName === 'HORN'
    || isTableComputedFromHdph;

  return (
    <div style={containerStyle}>
      <Paper elevation={0} sx={leftBoxStyle}>
        <div style={{ flexDirection: "row" }}>
          <TextField
            label="Drillhole Name"
            value={newDrillholeName}
            onChange={(event) => setNewDrillholeName(event.target.value)}
            sx={{ m: 1, width: "90%" }}
          />
          <Button
            variant="contained"
            onClick={() => {
              handleAddDrillHole();
            }}
            sx={{ m: 1, width: "90%" }}
            disabled={newDrillholeName === ""}
          >
            Add Drillhole
          </Button>
        </div>

        <RichTreeView
          items={DrillHoleName}
          selectedItems={selectedDrillHole}
          onItemSelectionToggle={handleItemSelectionToggle}
          onSelectedItemsChange={(_, ids) => {
            if (selectedDrillHole === ids) {
              setSelectedDrillHole(null)
            } else {
              setSelectedDrillHole(ids)
            }
          }}
          sx={{
            "& .MuiTreeItem-content": {
              padding: "4px 8px",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
              "&.Mui-selected": {
                backgroundColor: theme.palette.action.selected,
              },
            },
            "maxHeight": "63vh",
            "overflowY": "scroll",
          }}
        />
      </Paper>

      <div style={rightContentStyle}>
        <div style={tabContainerStyle}>
          {tabList.map((tabName, index) => (
            <TabItem
              tabName={tabName}
              currentValue={tabValue}
              index={index}
              onClick={handleTabClick}
              buttonOnClick={handelTabCloseClick}
              key={`tab${index}`}
            />
          ))}
        </div>

        <div style={tabPanelContainerStyle}>
          <Box sx={{ p: 2 }}>
            {tabList.map((tabName, index) => (
              <TabPanel
                tabName={tabName}
                currentValue={tabValue}
                index={index}
                key={`tabPanel${index}`}
                columns={currentData.columns}
                rows={currentData.rows}
                onTableValueChange={(newValue) =>
                  handleTableValueChange(tabName, newValue)
                }
                toolbarDisabled={areButtonsDisabled}
                handleAddRow={handleAddRow}
                handleDeleteRow={handleDeleteRow}
                rowSelectionModel={rowSelectionModel}
                setRowSelectionModel={setRowSelectionModel}
                footerText={status}
              />
            ))}

          </Box>
        </div>
      </div>
    </div>
  );
};

export default DataView;

function displaySaveConfirmation(setStatus) {
  setStatus("Changes saved");
  setTimeout(() => {
    setStatus("Ready");
  }, 3000);
}

